import { useState } from 'react';
import {
  Tabs,
  Table,
  TextInput,
  Select,
  Button,
  Group,
  Badge,
  Pagination,
  Card,
  Stack,
  Text,
  Loader,
  Alert,
  ActionIcon,
  Modal,
  Switch,
  Title
} from '@mantine/core';
import {
  IconSearch,
  IconEye,
  IconExclamationCircle,
  IconRefresh,
  IconCreditCard,
  IconCheck
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useApiClient, useLiveQuery } from '../../api';
import SubscriptionManager from './SubscriptionManager';

const PAGE_LIMIT = 20;

const UserManagement = () => {
  const api = useApiClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [userType, setUserType] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [updatingUser, setUpdatingUser] = useState(false);
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [subscriptionUserId, setSubscriptionUserId] = useState(null);
  const [subscriptionUsername, setSubscriptionUsername] = useState('');

  // Live: seller registrations/approvals change the nested seller_profile on
  // user rows, so the list refreshes on admin.sellers events.
  const { data, loading, error, refetch } = useLiveQuery('/api/v1/admin/users', {
    page: currentPage,
    limit: PAGE_LIMIT,
    search: submittedSearch || undefined,
    type: userType || undefined,
  }, {
    topics: ['admin.sellers'],
  });

  const users = data?.items ?? [];
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / (data?.limit ?? PAGE_LIMIT)));

  const handleSearch = () => {
    setCurrentPage(1);
    setSubmittedSearch(searchTerm);
  };

  const handleUserTypeChange = (value) => {
    setUserType(value);
    setCurrentPage(1);
  };

  const viewUserDetails = async (userId) => {
    try {
      const user = await api.get(`/api/v1/admin/users/${userId}`);
      setSelectedUser(user);
      setUserModalOpen(true);
    } catch (err) {
      console.error('Error fetching user details:', err);
      notifications.show({
        title: 'Error',
        message: err.message || 'Failed to load user details',
        color: 'red',
      });
    }
  };

  const openSubscriptionManager = (userId, displayName) => {
    setSubscriptionUserId(userId);
    setSubscriptionUsername(displayName);
    setSubscriptionModalOpen(true);
  };

  const updateUserField = async (field, value) => {
    if (!selectedUser) return;

    try {
      setUpdatingUser(true);
      const updated = await api.patch(`/api/v1/admin/users/${selectedUser.id}`, { [field]: value });
      setSelectedUser(updated);
      refetch();
      notifications.show({
        title: 'User updated',
        message: `Successfully updated ${field.replace(/_/g, ' ')}`,
        color: 'green',
        icon: <IconCheck size="1rem" />,
      });
    } catch (err) {
      console.error('Error updating user:', err);
      notifications.show({
        title: 'Update failed',
        message: err.message || 'Failed to update user',
        color: 'red',
      });
    } finally {
      setUpdatingUser(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const userTypeLabel = (user) => {
    if (user.is_admin) return 'Admin';
    if (user.seller_profile) return 'Seller';
    return 'User';
  };

  if (loading && users.length === 0) {
    return (
      <Tabs.Panel value="users" pt="xs">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Loader size="lg" />
        </div>
      </Tabs.Panel>
    );
  }

  return (
    <Tabs.Panel value="users" pt="xs">
      <Card shadow="sm" padding="lg" radius="md" withBorder mb="md">
        <Stack gap="md">
          <Group align="center" gap="md">
            <TextInput
              placeholder="Search users..."
              leftSection={<IconSearch size="0.9rem" />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              style={{ flex: 1 }}
            />
            <Select
              placeholder="User type"
              value={userType}
              onChange={handleUserTypeChange}
              data={[
                { value: '', label: 'All Users' },
                { value: 'admin', label: 'Admins' },
                { value: 'seller', label: 'Sellers' },
                { value: 'regular', label: 'Regular Users' }
              ]}
              clearable
            />
            <Button onClick={handleSearch} loading={loading}>
              Search
            </Button>
            <ActionIcon onClick={refetch} variant="light">
              <IconRefresh size="1rem" />
            </ActionIcon>
          </Group>

          {error && (
            <Alert icon={<IconExclamationCircle size="1rem" />} color="red">
              {error.message}
            </Alert>
          )}
        </Stack>
      </Card>

      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Display Name</Table.Th>
              <Table.Th>Email</Table.Th>
              <Table.Th>Name</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>Joined</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {users.map((user) => (
              <Table.Tr key={user.id}>
                <Table.Td>
                  <Text fw={500}>{user.display_name}</Text>
                </Table.Td>
                <Table.Td>{user.email || '—'}</Table.Td>
                <Table.Td>
                  {user.first_name || user.last_name
                    ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                    : '—'
                  }
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    {user.is_admin && (
                      <Badge color="pink" variant="light" size="sm">
                        Admin
                      </Badge>
                    )}
                    {user.seller_profile && (
                      <Badge
                        color={user.seller_profile.seller_status === 'approved' ? 'green' : 'yellow'}
                        variant="light"
                        size="sm"
                      >
                        Seller ({user.seller_profile.seller_status})
                      </Badge>
                    )}
                    {user.subscription && (
                      <Badge
                        color={
                          user.subscription.status === 'active'
                            ? 'grape'
                            : user.subscription.status === 'past_due'
                              ? 'yellow'
                              : 'gray'
                        }
                        variant="light"
                        size="sm"
                      >
                        {user.subscription.plan}
                      </Badge>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>{userTypeLabel(user)}</Table.Td>
                <Table.Td>{formatDate(user.created_at)}</Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <ActionIcon
                      variant="light"
                      onClick={() => viewUserDetails(user.id)}
                      title="View Details"
                    >
                      <IconEye size="1rem" />
                    </ActionIcon>
                    <ActionIcon
                      variant="light"
                      color="green"
                      onClick={() => openSubscriptionManager(user.id, user.display_name)}
                      title="Manage Subscription"
                    >
                      <IconCreditCard size="1rem" />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        {users.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <Text c="dimmed">No users found</Text>
          </div>
        )}

        {totalPages > 1 && (
          <Group align="center" mt="md">
            <Pagination
              value={currentPage}
              onChange={setCurrentPage}
              total={totalPages}
            />
          </Group>
        )}
      </Card>

      {/* User Details Modal */}
      <Modal
        opened={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        title={<Title order={3}>User Details</Title>}
        size="md"
      >
        {selectedUser && (
          <Stack gap="md">
            <Group align="apart">
              <Text fw={500}>Display Name:</Text>
              <Text>{selectedUser.display_name}</Text>
            </Group>

            <Group align="apart">
              <Text fw={500}>Clerk Username:</Text>
              <Text>{selectedUser.username || '—'}</Text>
            </Group>

            <Group align="apart">
              <Text fw={500}>Email:</Text>
              <Text>{selectedUser.email || '—'}</Text>
            </Group>

            <Group align="apart">
              <Text fw={500}>Full Name:</Text>
              <Text>
                {selectedUser.first_name || selectedUser.last_name
                  ? `${selectedUser.first_name || ''} ${selectedUser.last_name || ''}`.trim()
                  : '—'
                }
              </Text>
            </Group>

            <Group align="apart">
              <Text fw={500}>Date Joined:</Text>
              <Text>{formatDate(selectedUser.created_at)}</Text>
            </Group>

            {/* Admin Controls */}
            <Card withBorder p="md" mt="md">
              <Title order={4} mb="md">Admin Controls</Title>

              <Group align="apart">
                <Text>Admin Status</Text>
                <Switch
                  checked={selectedUser.is_admin}
                  onChange={(e) => updateUserField('is_admin', e.currentTarget.checked)}
                  disabled={updatingUser}
                />
              </Group>
            </Card>

            {/* Seller Information */}
            {selectedUser.seller_profile && (
              <Card withBorder p="md" mt="md">
                <Title order={4} mb="md">Seller Information</Title>
                <Stack gap="sm">
                  <Group align="apart">
                    <Text fw={500}>Status:</Text>
                    <Badge color={
                      selectedUser.seller_profile.seller_status === 'approved' ? 'green' :
                      selectedUser.seller_profile.seller_status === 'pending' ? 'yellow' : 'red'
                    }>
                      {selectedUser.seller_profile.seller_status}
                    </Badge>
                  </Group>
                  <Group align="apart">
                    <Text fw={500}>Verified:</Text>
                    <Badge color={selectedUser.seller_profile.is_verified ? 'green' : 'gray'}>
                      {selectedUser.seller_profile.is_verified ? 'Yes' : 'No'}
                    </Badge>
                  </Group>
                </Stack>
              </Card>
            )}

            {/* Subscription Information */}
            {selectedUser.subscription && (
              <Card withBorder p="md" mt="md">
                <Title order={4} mb="md">Subscription</Title>
                <Stack gap="sm">
                  <Group align="apart">
                    <Text fw={500}>Plan:</Text>
                    <Badge variant="filled">
                      {selectedUser.subscription.plan === 'paypiggie' ? 'PayPiggie' : 'PayPiggiePro'}
                    </Badge>
                  </Group>
                  <Group align="apart">
                    <Text fw={500}>Status:</Text>
                    <Badge
                      color={
                        selectedUser.subscription.status === 'active'
                          ? 'green'
                          : selectedUser.subscription.status === 'past_due'
                            ? 'yellow'
                            : 'gray'
                      }
                      variant="light"
                    >
                      {selectedUser.subscription.status}
                    </Badge>
                  </Group>
                </Stack>
              </Card>
            )}
          </Stack>
        )}
      </Modal>

      {/* Subscription Manager Modal */}
      <SubscriptionManager
        opened={subscriptionModalOpen}
        onClose={() => {
          setSubscriptionModalOpen(false);
          refetch();
        }}
        userId={subscriptionUserId}
        username={subscriptionUsername}
      />
    </Tabs.Panel>
  );
};

export default UserManagement;
